import { useState, useEffect } from 'react'
import { Percent, AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect } from '@/components/ui/native-select'
import { Textarea } from '@/components/ui/textarea'
import { FormField } from '@/components/common/FormSection'
import { financeV2Service } from '../services/financeV2Service'

import { getErrorMessage } from '../utils/error.utils'
interface CreateContingencyFeeModalProps {
  isOpen: boolean
  onClose: () => void
  caseId: string
  clientId: string
  caseTitle: string
  onSuccess: () => void
}

interface CostCenter {
  id: string
  name: string
}

export function CreateContingencyFeeModal({
  isOpen,
  onClose,
  caseId,
  clientId,
  caseTitle,
  onSuccess
}: CreateContingencyFeeModalProps) {
  const [percentage, setPercentage] = useState('30')
  const [description, setDescription] = useState('')
  const [costCenterId, setCostCenterId] = useState('')
  const [alertOnCaseClosure, setAlertOnCaseClosure] = useState(true)
  const [costCenters, setCostCenters] = useState<CostCenter[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen) {
      loadCostCenters()
      // Reset form
      setPercentage('30')
      setDescription('')
      setCostCenterId('')
      setAlertOnCaseClosure(true)
      setError('')
    }
  }, [isOpen])

  const loadCostCenters = async () => {
    try {
      const response = await financeV2Service.costCenters.list({ isActive: true })
      if (response.success) {
        setCostCenters(response.data.costCenters)
      }
    } catch (err) {
      console.error('Error loading cost centers:', err)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    const percentageNum = parseFloat(percentage)
    if (isNaN(percentageNum) || percentageNum <= 0 || percentageNum > 100) {
      setError('Percentual deve estar entre 0 e 100')
      return
    }

    if (!description.trim()) {
      setError('Descrição é obrigatória')
      return
    }

    try {
      setLoading(true)
      setError('')

      const response = await financeV2Service.contingencyFees.create({
        case_id: caseId,
        client_id: clientId,
        percentage: percentageNum,
        description: description.trim(),
        cost_center_id: costCenterId || undefined,
        alert_on_case_closure: alertOnCaseClosure
      })

      if (response.success) {
        onSuccess()
        onClose()
      } else {
        setError(response.message || 'Erro ao criar honorário')
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao criar honorário'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !loading) onClose() }}>
      <DialogContent size="md" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Percent className="size-4 text-muted-foreground" aria-hidden="true" />
            Criar Honorário Contingente
          </DialogTitle>
          <DialogDescription>Honorário de êxito calculado sobre o valor obtido no caso.</DialogDescription>
        </DialogHeader>

        <form id="create-contingency-fee-form" onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-4">
          <div className="rounded-lg border bg-muted/40 p-3">
            <p className="mb-1 text-sm font-medium">Caso</p>
            <p className="break-words text-sm text-muted-foreground">{caseTitle}</p>
          </div>

          {error && (
            <div role="alert" className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden="true" />
              <p className="text-destructive">{error}</p>
            </div>
          )}

          <FormField
            label="Percentual de Honorário *"
            htmlFor="percentage"
            hint="Percentual sobre o valor obtido no caso (0-100%)"
          >
            <div className="relative">
              <Input
                type="number"
                id="percentage"
                value={percentage}
                onChange={(e) => setPercentage(e.target.value)}
                min="0"
                max="100"
                step="0.01"
                required
                className="pr-8"
                placeholder="Ex: 30"
              />
              <Percent className="pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            </div>
          </FormField>

          <FormField label="Descrição *" htmlFor="description" hint="Descreva as condições do honorário">
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              required
              placeholder="Ex: Honorário de êxito - 30% do valor obtido na sentença"
            />
          </FormField>

          <FormField label="Centro de Custo (Opcional)" htmlFor="costCenter">
            <NativeSelect
              id="costCenter"
              value={costCenterId}
              onChange={(e) => setCostCenterId(e.target.value)}
            >
              <option value="">Selecione um centro de custo</option>
              {costCenters.map((cc) => (
                <option key={cc.id} value={cc.id}>
                  {cc.name}
                </option>
              ))}
            </NativeSelect>
          </FormField>

          <div className="flex items-start gap-3">
            <input
              id="alertOnCaseClosure"
              type="checkbox"
              checked={alertOnCaseClosure}
              onChange={(e) => setAlertOnCaseClosure(e.target.checked)}
              className="mt-0.5 size-4 shrink-0 rounded-sm border-input accent-primary"
            />
            <div className="flex min-w-0 flex-col gap-1">
              <Label htmlFor="alertOnCaseClosure">Notificar quando o caso for encerrado</Label>
              <p className="text-xs text-muted-foreground">
                Você receberá uma notificação automática quando o caso for finalizado ou arquivado
              </p>
            </div>
          </div>

          <div role="note" className="rounded-lg border bg-muted/40 p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-sm font-medium">Como funciona</p>
                <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-muted-foreground">
                  <li>O honorário fica com status &quot;Ativo&quot; até ser cobrado</li>
                  <li>Quando o caso for encerrado, você será notificado (se marcado)</li>
                  <li>Para cobrar, clique em &quot;Cobrar&quot; e informe o valor obtido</li>
                  <li>Uma transação financeira será criada automaticamente</li>
                </ul>
              </div>
            </div>
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" form="create-contingency-fee-form" loading={loading}>
            {loading ? 'Criando...' : 'Criar Honorário'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
